import { useState, useEffect } from 'react'
import { PayPalSDKManager, type FundingEligibility } from '../utils/paypal-sdk-manager'
import { FREQUENCY } from '@classy/campaign-page-blocks'
import paypalApi from 'redux/services/paypal'
import useConfig from 'modules/config/state/use-config'
import useOrder from 'modules/order/state/order/use-order'
import { PaymentMethod } from 'models/ChargeIntent'

interface PayPalFundingEligibilityState {
  isLoading: boolean
  isLoaded: boolean
  eligibility: FundingEligibility | null
  error: Error | null
}

/**
 * Hook to manage PayPal SDK loading and funding eligibility checking
 */
export const usePayPalFundingEligibility = () => {
  const config = useConfig()
  const order = useOrder()

  const [state, setState] = useState<PayPalFundingEligibilityState>({
    isLoading: false,
    isLoaded: false,
    eligibility: null,
    error: null,
  })

  // If the org does not support Venmo or this is a recurring payment, skip all this
  const venmoNotAllowed =
    order.frequency.value !== FREQUENCY.ONE_TIME ||
    config.payment.activeMethods.every((method) => method.type !== PaymentMethod.Venmo)

  // Get PayPal token data
  const { data: tokenData, error: tokenError } = paypalApi.useGetPaypalTokenQuery(
    {
      campaignId: config.campaignId,
      currency: order.currency.value,
    },
    {
      skip: venmoNotAllowed,
    },
  )

  // Load and check eligibility when token data is available
  useEffect(() => {
    if (!tokenData) {
      return
    }

    if (venmoNotAllowed) {
      setState({
        isLoading: false,
        isLoaded: true,
        eligibility: { paypal: true, venmo: false },
        error: null,
      })

      return
    }

    setState({
      isLoading: true,
      isLoaded: false,
      eligibility: null,
      error: null,
    })

    PayPalSDKManager.loadAndCheckEligibility({
      clientId: tokenData.clientId,
      merchantId: tokenData.merchantId,
      currency: order.currency.value,
      intent: 'capture',
    })
      .then((eligibility) => {
        setState({
          isLoading: false,
          isLoaded: true,
          eligibility,
          error: null,
        })
      })
      .catch((error) => {
        console.error('PayPal funding eligibility check failed:', error)

        setState({
          isLoading: false,
          isLoaded: false,
          eligibility: null,
          error,
        })
      })
      .finally(() => {
        // we've used the SDK for what we needed already
        // the actual buttons will load it independently via a React provider
        PayPalSDKManager.reset()
      })
  }, [tokenData, order.currency.value, order.frequency.value])

  // Handle token errors
  useEffect(() => {
    if (tokenError) {
      setState({
        isLoading: false,
        isLoaded: false,
        eligibility: null,
        error: new Error('Failed to get PayPal token'),
      })
    }
  }, [tokenError])

  return {
    ...state,
    isPayPalEligible: true,
    isVenmoEligible: state.eligibility?.venmo ?? false,
  }
}
